import { type SupportedLocale } from '@zep-us/i18n';
import { useRouter } from 'next/router';

import { logger } from '@zep/core-logger';

import { useLocale } from '@/hooks';
import { useUser } from '@/lib/auth';
import { type LanguageSelectorProps } from '@/shared/components/LanguageSelector';
import { LanguageCookieManager } from '@/shared/cookies';

export function useLanguageChange(
  eventProperties: LanguageSelectorProps['eventProps'],
) {
  const router = useRouter();
  const { locale: currentLocale } = useLocale();
  const { user } = useUser();

  const handleLanguageChange = async (
    locale: SupportedLocale | null,
    onChangeCallback?: LanguageSelectorProps['onChangeLanguageCallback'],
  ) => {
    if (locale && locale !== currentLocale) {
      // 언어 변경 시 쿠키에 저장
      LanguageCookieManager.setLanguage(locale);

      logger.track('language_change', {
        location: eventProperties.location,
        user_status: user ? 'logged_in' : 'logged_out',
        selected_language: locale,
        browser_language: navigator.language || 'unknown',
        previous_language: currentLocale,
      });

      // 풀 리로드로 전환 — 클라이언트 사이드 로케일 전환(router.push)은
      // _document를 재실행하지 않아 로케일별 폰트 CSS(<link>)와 <html lang>이
      // 이전 언어에 머물러 일본어 등에서 폰트가 깨져 보인다
      const { asPath, basePath, defaultLocale } = router;
      const localePrefix = locale === defaultLocale ? '' : `/${locale}`;
      window.location.assign(`${basePath ?? ''}${localePrefix}${asPath}`);
      if (onChangeCallback) {
        onChangeCallback(locale);
      }
    }
  };

  return {
    currentLocale,
    handleLanguageChange,
  };
}
